// Tableau de bord d'un projet : Adoption Score, radar des piliers, indicateurs.
//
// Le score reste en tête ; le reste se lit UN ONGLET À LA FOIS (études et
// orientation, tableau d'adoption, mesures produit, indicateurs). Ce qu'il
// reste à définir est passé dans l'ampoule du menu, à côté de la roue crantée.
//
// Tout ce qui s'affiche ici est calculé par le serveur. Le frontend ne fait
// aucune arithmétique de notation — c'est ce qui permet d'affirmer que le
// chiffre montré au client est celui que le moteur défendrait.
import { useState } from 'react';
import { useAdoption, useComputeAdoption, useMetricItems, useMetrics } from '../api/queries';
import type { Metric, MetricGroupKey, MetricItemTarget, PillarScore } from '../api/types';
import { Button, buttonClass, ErrorBanner, Eyebrow, IconButton, Spinner } from '../components/ui';
import { Link } from '../router';
import { ResearchStats, StudyPlanBlock } from '../plan/StudyPlanBlock';
import { TabBar, TabPanel, useTabs } from '../components/Tabs';
import { PillarModelBanner } from '../mesure0/PillarModelBanner';
import { EvolutionPanel } from './EvolutionPanel';
import { AdoptionPanel } from './AdoptionPanel';
import { BarometerBlock } from './BarometerBlock';
import { HeartGrid } from './HeartGrid';
import { MeasureHistorySection } from './MeasureHistory';
import { ObjectivesReminder } from './ObjectivesReminder';
import { StartHere } from './StartHere';
import './dashboard.css';

const GROUP_LABELS: Record<MetricGroupKey, string> = {
  evidence: 'Socle de preuve',
  traceability: 'Traçabilité',
  risk: 'Risque',
  coverage: 'Couverture',
  evaluation: 'Évaluation',
};

const GROUP_ORDER: MetricGroupKey[] = [
  'evidence',
  'traceability',
  'risk',
  'coverage',
  'evaluation',
];

function MetricCard({
  metric,
  open,
  onToggle,
}: {
  metric: Metric;
  open: boolean;
  onToggle: () => void;
}) {
  // `null` signifie « rien à mesurer », pas « zéro ». On affiche un tiret : un
  // « 0 % » sur un dénominateur vide est un mensonge par arrondi.
  const hasValue = metric.value !== null && metric.value !== undefined;
  const detail = metric.detail ?? (metric.of !== undefined ? `${metric.value} / ${metric.of}` : '');
  const trend = metric.trend;

  return (
    <li>
      {/* Toute la carte est un bouton : un clic ouvre, à droite, la liste de ce
          que le chiffre compte ; un second clic la referme. */}
      <button
        type="button"
        className={`metric${open ? ' metric-on' : ''}`}
        aria-pressed={open}
        onClick={onToggle}
      >
        <span className="metric-label">{metric.label}</span>
        <span className="metric-row">
          <span className={`metric-value tone-${hasValue ? (metric.tone ?? 'neutral') : 'zy-empty'}`}>
            {hasValue ? metric.value : '—'}
            {hasValue && metric.unit ? <span className="metric-unit">{metric.unit}</span> : null}
          </span>
          {/* Une tendance dont le pourcentage est `null` n'en est pas une : la
              période précédente était vide. On ne l'affiche pas. */}
          {trend && trend.pct !== null ? (
            <span className={`metric-trend tone-${trend.tone}`}>{trend.label}</span>
          ) : null}
        </span>
        {detail ? <span className="metric-detail">{detail}</span> : null}
        {metric.hint ? <span className="metric-hint">{metric.hint}</span> : null}
      </button>
    </li>
  );
}

/** La page où s'ouvre un élément : sa fiche, ou la section qui le contient. */
function targetPath(projectId: string, target: MetricItemTarget): string {
  const base = `/projet/${encodeURIComponent(projectId)}`;
  const id = encodeURIComponent(target.id);
  switch (target.kind) {
    case 'interview':
      return `${base}/entretiens/${id}`;
    case 'study':
      return `${base}/etudes/${id}`;
    case 'test':
      return `${base}/tests/${id}`;
    default:
      return `${base}/${id}`;
  }
}

const TARGET_LABELS: Record<MetricItemTarget['kind'], string> = {
  interview: 'Ouvrir l’entretien',
  study: 'Ouvrir l’étude',
  test: 'Ouvrir le test',
  section: 'Ouvrir',
};

// Le panneau de droite : ce qu'il y a derrière l'indicateur ouvert. Chaque
// ligne est un vrai lien vers l'endroit où l'élément vit (entretien, étude,
// test) — c'est le second clic qui y emmène.
function MetricItemsPanel({
  projectId,
  metricKey,
  onClose,
}: {
  projectId: string;
  metricKey: string;
  onClose: () => void;
}) {
  const items = useMetricItems(projectId, metricKey);

  return (
    <aside className="zy-card metric-panel" aria-live="polite">
      <div className="metric-panel-head">
        <h3 className="t-h3">
          {items.data?.title ?? 'Chargement…'}
          {items.data ? <span className="metric-panel-count">{items.data.total}</span> : null}
        </h3>
        <IconButton icon="close" onClick={onClose} label="Fermer la liste" />
      </div>

      {items.isPending ? <Spinner label="Chargement de la liste…" /> : null}
      {items.isError ? (
        <ErrorBanner>
          {items.error instanceof Error ? items.error.message : 'Impossible de charger cette liste.'}
        </ErrorBanner>
      ) : null}
      {items.data && items.data.items.length === 0 ? (
        <p className="metric-panel-empty">{items.data.empty}</p>
      ) : null}

      {items.data && items.data.items.length > 0 ? (
        <ul className="metric-items">
          {items.data.items.map((it) => {
            const body = (
              <>
                <span className="metric-item-title">{it.title}</span>
                {it.sub ? <span className="metric-item-sub">{it.sub}</span> : null}
                {it.target ? (
                  <span className="metric-item-go">{TARGET_LABELS[it.target.kind]} →</span>
                ) : null}
              </>
            );
            return (
              <li key={it.id}>
                {it.target ? (
                  <Link to={targetPath(projectId, it.target)} className="metric-item metric-item-link">
                    {body}
                  </Link>
                ) : (
                  <div className="metric-item">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {/* Le serveur plafonne la liste : on le dit, plutôt que de laisser croire
          qu'elle est complète. */}
      {items.data && items.data.total > items.data.items.length ? (
        <p className="metric-panel-empty">
          {items.data.items.length} affichés sur {items.data.total}.
        </p>
      ) : null}
    </aside>
  );
}

function ScoreHeader({
  score,
  confidence,
  capped,
  cappedPillars,
  scored,
  unscored,
  awaitingBuild,
}: {
  score: number | null;
  confidence: number | null;
  capped: boolean;
  cappedPillars: string[];
  scored: number;
  unscored: number;
  awaitingBuild: boolean;
}) {
  return (
    <div className="score-strip">
      <div className="strip-cell">
        <Eyebrow>Score d’adoption</Eyebrow>
        {score === null ? (
          <>
            <span className="big big-empty">—</span>
            <p className="zy-texte-muet">
              {awaitingBuild
                ? 'Matière suffisante — lancez la construction du tableau'
                : 'Pas encore calculé'}
            </p>
          </>
        ) : (
          <>
            <span className="big">
              {score}
              <span className="big-unit">/100</span>
            </span>
            <p className="zy-texte-muet">
              {scored} pilier{scored > 1 ? 's' : ''} noté{scored > 1 ? 's' : ''}
              {unscored > 0 ? ` · ${unscored} sans preuve` : ''}
            </p>
          </>
        )}
      </div>

      <div className="strip-cell">
        <Eyebrow>Indice de confiance</Eyebrow>
        <span className="big">
          {confidence === null ? '—' : confidence}
          {confidence === null ? null : <span className="big-unit">%</span>}
        </span>
        <progress className="zy-progress" value={confidence ?? 0} max={100} aria-label="Indice de confiance" />
        <p className="zy-texte-muet">Volume et croisement des preuves</p>
      </div>

      {capped ? (
        <div className="strip-cell strip-cell-warn">
          <Eyebrow>Plafond actif</Eyebrow>
          <p className="strip-warn-title">
            {cappedPillars.length === 1
              ? cappedPillars[0]
              : `${cappedPillars.length} piliers en signal critique`}
          </p>
          <p className="zy-texte-muet">
            Signal critique sur {cappedPillars.join(', ')} — le score global est plafonné à 68 tant
            que ces objectifs ne sont pas rétablis.
          </p>
        </div>
      ) : (
        <div className="strip-cell">
          <Eyebrow>Signal critique</Eyebrow>
          <p className="strip-warn-title strip-ok">Aucun</p>
          <p className="zy-texte-muet">Aucun pilier ne plafonne le score global.</p>
        </div>
      )}
    </div>
  );
}

function PillarList({
  pillars,
  selected,
  onSelect,
}: {
  pillars: PillarScore[];
  selected: number;
  onSelect: (i: number) => void;
}) {
  return (
    <ol className="pillars">
      {pillars.map((p, i) => (
        <li key={p.pillarId}>
          <button
            type="button"
            className={`pillar${i === selected ? ' pillar-on' : ''}`}
            onClick={() => onSelect(i)}
            aria-current={i === selected}
          >
            <span className="pillar-num">{String(i + 1).padStart(2, '0')}</span>
            <span className="pillar-body">
              <span className="pillar-name-row">
                <span className="pillar-name">{p.name}</span>
                {p.isCapped ? <span className="pillar-cap">Plafond</span> : null}
              </span>
              <span className="pillar-bar">
                <span
                  className={`pillar-fill${p.isCapped ? ' pillar-fill-cap' : ''}`}
                  style={{ width: `${(p.score ?? 0) * 10}%` }}
                />
              </span>
            </span>
            <span className="pillar-score">
              {p.score?.toFixed(1)}
              <span className="pillar-score-unit">/10</span>
            </span>
            <span className={`pillar-delta${p.delta === null ? ' pillar-delta-none' : p.delta > 0 ? ' tone-good' : p.delta < 0 ? ' tone-bad' : ''}`}>
              {p.delta === null ? '—' : `${p.delta > 0 ? '+' : ''}${p.delta}`}
            </span>
            <span className="pillar-weight">×{p.weight}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}

const DASHBOARD_TABS = ['orientation', 'evolution', 'adoption', 'mesures', 'indicateurs'] as const;

export function ProjectDashboard({ projectId, projectName }: { projectId: string; projectName: string }) {
  const metrics = useMetrics(projectId);
  const adoption = useAdoption(projectId);
  const compute = useComputeAdoption(projectId);
  const [selected, setSelected] = useState(0);
  // L'indicateur dont la liste est ouverte à droite (aucun au départ).
  const [openMetric, setOpenMetric] = useState<string | null>(null);
  const [tab, setTab] = useTabs('dashboard', DASHBOARD_TABS, 'orientation');

  if (metrics.isPending || adoption.isPending) {
    return (
      <div className="app-loading">
        <Spinner label="Chargement du projet…" />
      </div>
    );
  }

  if (metrics.isError || adoption.isError) {
    const error = metrics.error ?? adoption.error;
    return (
      <div className="app-main">
        <ErrorBanner>
          {error instanceof Error ? error.message : 'Impossible de charger ce projet.'}
        </ErrorBanner>
        <Link to="/" className={buttonClass('ghost')}>
          Retour au portefeuille
        </Link>
      </div>
    );
  }

  // Seuls les piliers NOTÉS alimentent le radar : un objectif sans preuve n'y
  // figure pas à zéro, il n'y figure pas du tout.
  const scoredPillars = adoption.data.pillars.filter((p) => p.score !== null);
  const cappedNames = scoredPillars.filter((p) => p.isCapped).map((p) => p.name);
  const current = scoredPillars[Math.min(selected, Math.max(0, scoredPillars.length - 1))];
  const readiness = metrics.data.adoption;
  const scoreHeader = (
    <ScoreHeader
      score={adoption.data.global.val}
      confidence={adoption.data.global.confidence}
      capped={adoption.data.global.isCapped}
      cappedPillars={cappedNames}
      scored={adoption.data.global.scored}
      unscored={adoption.data.global.unscored}
      awaitingBuild={readiness.ready && adoption.data.global.val === null}
    />
  );

  return (
    <div className="app-main">
      <div className="zy-page-head__texte">
        <Link to="/" className="zy-retour back-link-desktop">
          ← Tous les projets
        </Link>
        <Eyebrow>Projet</Eyebrow>
        <h1 className="t-h1">{projectName}</h1>
      </div>

      {/* D'abord, la recherche en chiffres : c'est elle qui fait avancer le projet. */}
      <ResearchStats projectId={projectId} />

      {/* Un projet d'avant les modèles : ses piliers restent à confirmer. */}
      <PillarModelBanner projectId={projectId} projectName={projectName} />

      {/* La prochaine chose à faire : la mesure zéro, puis créer une étude. */}
      <StartHere projectId={projectId} projectName={projectName} />

      {/* L'Adoption Score ne s'affiche d'office qu'une fois calculé. Avant, des
          tirets et des zéros n'apprennent rien : il reste replié, à un clic. */}
      {adoption.data.global.val !== null ? (
        scoreHeader
      ) : (
        <details className="score-fold">
          <summary>
            Adoption Score <span className="score-fold-note">· pas encore de données</span>
          </summary>
          {scoreHeader}
        </details>
      )}

      <TabBar
        label="Sections du tableau de bord"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'orientation', label: 'Études et orientation' },
          { id: 'evolution', label: 'Évolution' },
          { id: 'adoption', label: 'Tableau d’adoption' },
          { id: 'mesures', label: 'Mesures produit' },
          { id: 'indicateurs', label: 'Indicateurs du projet' },
        ]}
      />

      {tab === 'orientation' ? (
        <TabPanel id="orientation">
          {/* Ce que la recherche doit servir (migration 049) : déclaré dans les Options. */}
          <ObjectivesReminder projectId={projectId} />
          <StudyPlanBlock projectId={projectId} />
        </TabPanel>
      ) : null}

      {/* L'adoption mois après mois, et les alertes (059). */}
      {tab === 'evolution' ? (
        <TabPanel id="evolution">
          <EvolutionPanel projectId={projectId} />
        </TabPanel>
      ) : null}

      {tab === 'adoption' ? (
      <TabPanel id="adoption">
      {adoption.data.model ? (
        <p className="t-mono">
          Modèle {adoption.data.model.toUpperCase()} · {adoption.data.global.scored} pilier{adoption.data.global.scored > 1 ? 's' : ''} noté{adoption.data.global.scored > 1 ? 's' : ''} sur {adoption.data.pillars.length}
        </p>
      ) : null}
      <AdoptionPanel
        readiness={readiness}
        computedAt={adoption.data.computedAt}
        onBuild={() => compute.mutate()}
        building={compute.isPending}
        error={compute.error}
      />

      {scoredPillars.length > 0 ? (
        <section className="zy-card pillars-panel">
          <PillarList pillars={scoredPillars} selected={selected} onSelect={setSelected} />
        </section>
      ) : (
        <section className="zy-card empty-pillars">
          <h2 className="t-h3">Aucun pilier noté pour l’instant</h2>
          <p className="card-lede">
            Les piliers d’adoption découlent du modèle de votre produit. Ils sont notés à partir du
            baromètre, des données mesurées, des tests et des faits qui leur sont rattachés.
          </p>
        </section>
      )}

      {current ? (
        <section className="zy-card selected-pillar">
          <div className="selected-head">
            <div>
              <Eyebrow>Pilier sélectionné</Eyebrow>
              <h2 className="t-h3">{current.name}</h2>
            </div>
            <p className="selected-meta">
              Note <strong>{current.score?.toFixed(1)}</strong>/10 · confiance{' '}
              <strong>{current.confidence}%</strong> · poids ×{current.weight}
              {current.sampleSize > 0 ? (
                <>
                  {' · '}
                  {current.sampleSize}
                  {current.sampleSize > 1 ? ' signaux rattachés' : ' signal rattaché'}
                </>
              ) : null}
            </p>
          </div>
          {current.objective ? <p className="selected-objective">{current.objective}</p> : null}
          {current.measures && current.measures.length > 0 ? (
            <ul className="pillar-sources">
              {current.measures.map((m) => (
                <li key={m.name} className="zy-texte-muet">
                  {m.name} : {m.value.toLocaleString('fr-FR')}, soit {m.score.toLocaleString('fr-FR')}/10 ({m.rule})
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      <HeartGrid projectId={projectId} />

      </TabPanel>
      ) : null}

      {/* Les chiffres produit (connecteurs, saisies) et leur historique : la
          base de l'avant/après d'une hypothèse. */}
      {tab === 'mesures' ? (
        <TabPanel id="mesures">
          <BarometerBlock projectId={projectId} />
          <MeasureHistorySection projectId={projectId} />
        </TabPanel>
      ) : null}

      {tab === 'indicateurs' ? (
      <TabPanel id="indicateurs">
      <section className="metrics">
        <div className="metrics-head">
          <Eyebrow>Santé de la recherche</Eyebrow>
          <h2 className="t-h2">Indicateurs du projet</h2>
          <span className="t-mono">tendances sur {metrics.data.metrics.periodDays} jours</span>
          <Button variant="ghost" onClick={() => void metrics.refetch()}>
            Actualiser
          </Button>
        </div>
        <div className={`metrics-body${openMetric ? ' metrics-body-open' : ''}`}>
          <div>
            {GROUP_ORDER.map((key) => {
              const items = metrics.data.metrics[key];
              if (!items || items.length === 0) return null;
              return (
                <div key={key} className="metric-group">
                  <h3 className="t-mono metric-group-title">{GROUP_LABELS[key]}</h3>
                  <ul className="metric-grid">
                    {items.map((m) => (
                      <MetricCard
                        key={m.key}
                        metric={m}
                        open={openMetric === m.key}
                        onToggle={() => setOpenMetric((k) => (k === m.key ? null : m.key))}
                      />
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          {openMetric ? (
            <MetricItemsPanel
              projectId={projectId}
              metricKey={openMetric}
              onClose={() => setOpenMetric(null)}
            />
          ) : null}
        </div>
      </section>
      </TabPanel>
      ) : null}
    </div>
  );
}
